import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble, Button, Modal, Select } from 'tea-component';
import { IHistoryType } from '../../types';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useIntl } from 'umi';
import {
  useHistoryFilterQuery,
  IFilterItem,
} from '../../hooks/useHistoryFilter';
import './index.scss';
import SavePopup from './SavePopup';
import { resumeDateTime } from '../../utils/time';

/**
 * 历史筛选器选择组件
 *
 * 注意点：
 *     删除后只是数据上的删除，并不会去请求过滤器列表，主要兼容本地请求和父级传入这两个情况
 *
 * @param confirm - 选中某个过滤器时的回调方法
 * @param options - 可选参数，如果传入就不再发起请求获取数据，否则就需要发起请求
 * @param setDisabledSaveBtn - 设置保存按钮禁用状态的方法
 * @param finallyDealwithHistoryValue - 最终处理历史值的函数，默认直接返回原值
 * @param historyFilterChange - 历史筛选器变更时的回调方法
 */
export default function HistorySelect({
  confirm,
  setDisabledSaveBtn,
  finallyDealwithHistoryValue = (value: any) => value,
  historyFilterChange,
  close,
  saveOpen,
  renderConfig,
  filterValue,
  pagePath,
  selectFilter,
  setSelectFilter,
}: IHistoryType) {
  // TODO: 筛选器保存长度
  const saveFilterLength = 50;

  /** 使用 useHistoryFilterQuery Hook 管理筛选器列表 */
  const {
    filterList,
    deleteFilterItem: deleteFilterById,
    prependFilterItem,
  } = useHistoryFilterQuery({ maxSaveCount: saveFilterLength, pagePath });

  /** 删除弹窗相关 */
  const [visible, setVisible] = useState(false);
  const toggleModal = () => {
    setVisible(!visible);
  };
  const { formatMessage } = useIntl();

  // 删除的过滤器的数据信息，主要用作弹窗展示以及删除请求的id
  const deleteFilterItem = useRef<{ name: string; id: number }>({
    name: '',
    id: -1,
  });

  useEffect(() => {
    setDisabledSaveBtn?.(filterList.length >= saveFilterLength);
  }, [filterList, saveFilterLength]);

  // 将filterList数据转化为select组件使用的格式
  const getOptionsItem = (basic: IFilterItem) => {
    const { filter_id, filter_name, filter_cond } = basic || {};

    return {
      value: String(filter_id),
      text: (
        <div className="dbom-select-filter-tab">
          <Bubble dark content={filter_name} closeOnScroll>
            <span className="tab-text">{filter_name}</span>
          </Bubble>

          <Bubble closeOnScroll dark content={formatMessage({ id: 'Delete' })}>
            <i
              className="tab-icon"
              onClick={(e: any) => {
                e?.stopPropagation();
                deleteFilterItem.current = { name: filter_name, id: filter_id };
                setVisible(true);
              }}
            >
              <SvgIcon type="clear-icon" />
            </i>
          </Bubble>
        </div>
      ),
      context: filter_name,
      data: filter_cond,
    };
  };
  // select组件最终渲染数据
  const selectOptionsElements = useMemo(() => {
    return filterList.map(getOptionsItem);
  }, [filterList, formatMessage]);

  // 获取select框选中内容
  const getSelectButton = () => {
    const selectedFilter = filterList.find(
      (item) => item.filter_id === Number(selectFilter),
    );
    return (
      <div className="dbom-select-filter-flex">
        <SvgIcon type="funnel" className="dbom-select-filter-icon" />
        <span className="dbom-select-filter-text">
          {formatMessage({ id: 'My Filter' })}
        </span>
        <span className="dbom-action-text">{selectedFilter?.filter_name}</span>
      </div>
    );
  };

  // 删除过滤器的请求方法
  const deleteSaveFilter = async () => {
    const id = deleteFilterItem.current?.id;
    await deleteFilterById(Number(id));
    toggleModal();
  };

  /** 父组件操作 */
  // 过滤器修改事件
  const filterChange = (value: string, content: any) => {
    let filterInfo: any = null;
    try {
      filterInfo = JSON.parse(content?.option?.data);

      // TODO: 这里要加上针对日期的处理方法
      const filterTime = resumeDateTime(filterInfo);
      filterInfo = { ...filterInfo, ...filterTime };
      if (finallyDealwithHistoryValue) {
        filterInfo = finallyDealwithHistoryValue(filterInfo);
      }
    } catch (error) {
      console.error('筛选器内容解析失败');
      return;
    }
    historyFilterChange?.(value, content);
    confirm?.(filterInfo);
    setSelectFilter?.(value);
  };
  // 记录select挂载位置
  const historySelectRef = useRef(null);

  return (
    <div className="history-select-wrapper" ref={historySelectRef}>
      <Select
        className="dbom-select-filter"
        appearance="button"
        boxClassName="dbom-select-filter-box"
        searchable
        listWidth={230}
        listHeight={220}
        options={selectOptionsElements}
        value={selectFilter}
        onChange={(value: string, content: any) => {
          filterChange(value, content);
        }}
        footer={
          <div className="dbom-searchbox-label">
            {formatMessage({ id: 'Up to 50 settings can be saved' })}
          </div>
        }
        button={getSelectButton}
      />
      <Modal
        visible={visible}
        onClose={() => {
          setVisible(false);
        }}
      >
        <Modal.Body>
          <div className="dbom-history-deleteSetting-title">
            {formatMessage({
              id: 'Delete the filter setting',
            })}
          </div>
          <div className="dbom-history-deleteSetting-desc">
            {formatMessage({
              id: 'Do you want to delete?',
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="primary" onClick={() => void deleteSaveFilter()}>
            {formatMessage({
              id: 'Confirm',
            })}
          </Button>
          <Button type="weak" onClick={() => setVisible(false)}>
            {formatMessage({
              id: 'Cancel',
            })}
          </Button>
        </Modal.Footer>
      </Modal>
      {saveOpen && (
        <SavePopup
          saveOpen={saveOpen}
          close={() => close()}
          renderConfig={renderConfig}
          filterValue={filterValue}
          pagePath={pagePath}
          onSuccess={(data: any) => {
            close();
            prependFilterItem(data);
            filterChange(String(data.filter_id), {
              option: { data: data.filter_cond },
            });
          }}
        ></SavePopup>
      )}
    </div>
  );
}
